import { KeywordResult } from "client-server-shared/types/keywords";
import { SearchPageItem } from "client-server-shared/types/search";
import type {
  AnalyzeContentParams,
  SEOResultResponse,
} from "server/load-url-content/analyze";
import { apiClient, fetchClient } from "./client-axios";
import { contentAuditUse } from "utils/tracking/common";
import {
  interactiveTextOperation,
} from "client-server-shared/constants";
import {
  CompetitorTermsClientResponse,
  KeywordDensity,
} from "client-server-shared/types/types";

const generateBlogImages = async ({
  description,
}: {
  description: string;
}): Promise<{ url: string; path: string; alt: string }[]> => {
  return (
    await apiClient.post("/api/free-tools/images", {
      description: description,
    })
  ).data.data;
};

const conductInternalLinking = async ({
  content,
  links,
}: {
  content: string;
  links: string[];
}) => {
  return fetchClient.post(`/api/free-tools/internal-linking`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      content,
      links,
    }),
  });
};

const onFreeToolGenerate = async (rest: any = {}) => {
  return fetchClient.post(`/api/free-tools/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(rest),
  });
};

const improveReadability = async ({ content }: { content: string }) => {
  return onFreeToolGenerate({
    content,
    op: interactiveTextOperation.readability,
  });
};

const humanize = async ({ content }: { content: string }) => {
  return onFreeToolGenerate({
    content,
    op: interactiveTextOperation.humanize,
  });
};

const generatePrompts = async ({ content }: { content: string }) => {
  return onFreeToolGenerate({
    content,
    op: interactiveTextOperation.prompt,
  });
};

const generateMetaDescription = async ({
  content,
  language,
}: {
  content: string;
  language?: string;
}) => {
  return onFreeToolGenerate({
    content,
    op: interactiveTextOperation.metaDescription,
    language,
  });
};

const paraphraseText = async ({ content }: { content: string }) => {
  return onFreeToolGenerate({
    content,
    op: interactiveTextOperation.paraphrase,
  });
};

const generateMetaTitle = async ({
  content,
  language,
}: {
  content: string;
  language?: string;
}) => {
  return onFreeToolGenerate({
    content,
    language,
    op: interactiveTextOperation.metaTitle,
  });
};

const expandText = async ({
  content,
  backgroundInfo,
  toneOfVoice,
  language,
}: {
  backgroundInfo?: string;
  toneOfVoice?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    backgroundInfo,
    toneOfVoice,
    language,
    op: interactiveTextOperation.expand,
  });
};

const emailReply = async ({
  content,
  backgroundInfo,
  language,
}: {
  backgroundInfo?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    backgroundInfo,
    language,
    op: interactiveTextOperation.emailReply,
  });
};

const customerResponse = async ({
  content,
  backgroundInfo,
  language,
}: {
  backgroundInfo?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    backgroundInfo,
    language,
    op: interactiveTextOperation.customerResponse,
  });
};

const redditComment = async ({
  content,
  backgroundInfo,
  language,
}: {
  backgroundInfo?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    backgroundInfo,
    language,
    op: interactiveTextOperation.redditComment,
  });
};

const twitterReply = async ({
  content,
  backgroundInfo,
  language,
}: {
  backgroundInfo?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    backgroundInfo,
    language,
    op: interactiveTextOperation.twitterReply,
  });
};

const nicheIdeas = async ({
  content,
  backgroundInfo,
  language,
}: {
  backgroundInfo?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    backgroundInfo,
    language,
    op: interactiveTextOperation.nicheIdeas,
  });
};

const valueProposition = async ({
  content,
  backgroundInfo,
  toneOfVoice,
  language,
}: {
  backgroundInfo?: string;
  language?: string;
  content: string;
  toneOfVoice?: string;
}) => {
  return onFreeToolGenerate({
    content,
    backgroundInfo,
    toneOfVoice,
    language,
    op: interactiveTextOperation.valueProposition,
  });
};

const proposal = async ({
  content,
  backgroundInfo,
  toneOfVoice,
  language,
}: {
  backgroundInfo?: string;
  language?: string;
  content: string;
  toneOfVoice?: string;
}) => {
  return onFreeToolGenerate({
    content,
    backgroundInfo,
    toneOfVoice,
    language,
    op: interactiveTextOperation.proposal,
  });
};

const reword = async ({
  content,
  toneOfVoice,
  language,
}: {
  toneOfVoice?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    toneOfVoice,
    language,
    op: interactiveTextOperation.reword,
  });
};

const generateIntroduction = async ({
  content,
  toneOfVoice,
  language,
}: {
  toneOfVoice?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    toneOfVoice,
    language,
    op: interactiveTextOperation.introduction,
  });
};
const generateSlogans = async ({
  content,
  toneOfVoice,
  language,
}: {
  toneOfVoice?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    toneOfVoice,
    language,
    op: interactiveTextOperation.slogan,
  });
};

const generateResignationLetter = async ({
  content,
  toneOfVoice,
  language,
}: {
  toneOfVoice?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    toneOfVoice,
    language,
    op: interactiveTextOperation.resignationLetter,
  });
};

const generateCallToAction = async ({
  content,
  language,
}: {
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    language,
    op: interactiveTextOperation.callToAction,
  });
};

const generateBusinessNames = async ({
  content,
  language,
}: {
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    language,
    op: interactiveTextOperation.businessName,
  });
};

const generateHook = async ({
  content,
  toneOfVoice,
  language,
}: {
  toneOfVoice?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    toneOfVoice,
    language,
    op: interactiveTextOperation.hook,
  });
};

const generateAcronyms = async ({
  content,
  language,
}: {
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    language,
    op: interactiveTextOperation.acronyms,
  });
};

const spellingGrammer = async ({
  content,
  language,
}: {
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    language,
    op: interactiveTextOperation.spellingGrammer,
  });
};

const conclusion = async ({
  content,
  toneOfVoice,
  language,
}: {
  toneOfVoice?: string;
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    toneOfVoice,
    language,
    op: interactiveTextOperation.conclusion,
  });
};

const simplifyText = async ({
  content,
  language,
}: {
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    language,
    op: interactiveTextOperation.simplifyText,
  });
};

const activeToPassiveVoice = async ({
  content,
  language,
}: {
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    language,
    op: interactiveTextOperation.activeToPassiveVoice,
  });
};

const passiveToActiveVoice = async ({
  content,
  language,
}: {
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    language,
    op: interactiveTextOperation.passiveToActiveVoice,
  });
};

const shorten = async ({
  content,
  toneOfVoice,
}: {
  content: string;
  toneOfVoice?: string;
}) => {
  return onFreeToolGenerate({
    content,
    toneOfVoice,
    op: interactiveTextOperation.shorten,
  });
};

const bookIdea = async ({ content }: { content: string }) => {
  return onFreeToolGenerate({
    content,
    op: interactiveTextOperation.bookIdea,
  });
};

const bookTitle = async ({ content }: { content: string }) => {
  return onFreeToolGenerate({
    content,
    op: interactiveTextOperation.bookTitle,
  });
};

const generateProsAndCons = async ({
  content,
  language,
}: {
  language?: string;
  content: string;
}) => {
  return onFreeToolGenerate({
    content,
    language,
    op: interactiveTextOperation.prosAndCons,
  });
};

const getKeywordsForKeywords = async (
  query: string,
  locations?: string[]
): Promise<KeywordResult[]> => {
  return (
    await apiClient.post(`/api/free-tools/keyword-research`, {
      query: query,
      locations,
    })
  ).data.data;
};

const analyzeContent = async (
  params: AnalyzeContentParams & {
    generateSecondaryKeywords: boolean;
  }
): Promise<
  SEOResultResponse & {
    secondaryKeywords?: string[];
  }
> => {
  contentAuditUse({
    type: "analyze-content",
  });
  return (
    await apiClient.post(`/api/free-tools/content-analyze`, {
      ...params,
    })
  ).data.data;
};

const analyzeCompetitors = async (params: {
  mainKeyword: string;
  secondaryKeywords?: string[] | null;
  language?: string | null;
  generateSecondaryKeywords: boolean;
  location?: string;
}): Promise<{
  items: SearchPageItem[];
  secondaryKeywords?: string[];
}> => {
  contentAuditUse({
    type: "analyze-competitors",
  });
  return (
    await apiClient.post(`/api/free-tools/competitors`, {
      ...params,
    })
  ).data.data;
};

const suggestKeywords = async (params: {
  mainKeyword: string;
  language?: string;
}): Promise<string[]> => {
  return (
    await apiClient.post(`/api/free-tools/keywords`, {
      ...params,
    })
  ).data.data;
};

const generateHeadlines = async (topic: string) => {
  return (
    await apiClient.post(`/api/free-tools/headlines`, {
      topic: topic,
    })
  ).data.data;
};

const getCompetitorsTerms = async ({
  topic,
  language,
  content,
  location,
}: {
  topic: string;
  language?: string;
  content?: string;
  location?: string;
}): Promise<CompetitorTermsClientResponse[]> => {
  return (
    await apiClient.post(`/api/free-tools/terms`, {
      mainKeyword: topic.trim(),
      language,
      content,
      location,
    })
  ).data.data;
};

const getKeywordsDensity = async ({
  keywords,
  language,
  content,
}: {
  keywords: string[];
  language?: string;
  content: string;
}): Promise<KeywordDensity & { text: string }[]> => {
  return (
    await apiClient.post(`/api/free-tools/keyword-density`, {
      keywords,
      language: language,
      content,
    })
  ).data.data;
};

export default {
  getKeywordsForKeywords,
  conductInternalLinking,
  generateBlogImages,
  analyzeContent,
  analyzeCompetitors,
  suggestKeywords,
  generateHeadlines,
  improveReadability,
  expandText,
  generateMetaDescription,
  generateMetaTitle,
  getCompetitorsTerms,
  getKeywordsDensity,
  paraphraseText,
  generatePrompts,
  generateIntroduction,
  humanize,
  generateSlogans,
  generateProsAndCons,
  reword,
  simplifyText,
  activeToPassiveVoice,
  passiveToActiveVoice,
  shorten,
  bookIdea,
  bookTitle,
  nicheIdeas,
  valueProposition,
  generateResignationLetter,
  proposal,
  generateCallToAction,
  generateBusinessNames,
  generateHook,
  generateAcronyms,
  spellingGrammer,
  conclusion,
  emailReply,
  customerResponse,
  redditComment,
  twitterReply,
  onFreeToolGenerate,
};
